/**
 * CharacterScreen — one question at a time.
 *
 * Sub-cards:
 *  0  peacemaker        yes/no
 *  1  homebuilder       yes/no
 *  2  humorous          yes/no
 *  3  caringAffectionate yes/no
 *  4  wantsPeacefulHome  yes/no
 *  5  goodManners        yes/no
 *  6  honestyRating      1–10 scale (last card)
 */
import { useState } from "react";
import { Button } from "../components/Button";
import { QuestionCard } from "../components/QuestionCard";
import { useFlow } from "../flow/FlowContext";
import { useT } from "../i18n";

const TOTAL = 7;

function YesNoCard({
  question,
  pos,
  value,
  onYes,
  onNo,
  onBack,
  showBack,
}: {
  question: string;
  pos: string;
  value: boolean | null;
  onYes: () => void;
  onNo: () => void;
  onBack: () => void;
  showBack: boolean;
}) {
  const t = useT();
  return (
    <QuestionCard question={question} position={pos}>
      <div className="q-options">
        <button
          type="button"
          className={`q-option${value === true ? " is-selected" : ""}`}
          onClick={onYes}
          aria-pressed={value === true}
        >
          <strong>{t("common.yes")}</strong>
        </button>
        <button
          type="button"
          className={`q-option${value === false ? " is-selected" : ""}`}
          onClick={onNo}
          aria-pressed={value === false}
        >
          <strong>{t("common.no")}</strong>
        </button>
      </div>
      {showBack && (
        <button type="button" className="q-subnav__back" style={{ marginTop: 16 }} onClick={onBack}>
          ← {t("shell.back")}
        </button>
      )}
    </QuestionCard>
  );
}

export function CharacterScreen() {
  const { profileAnswers, setProfileAnswers, next, canContinue } = useFlow();
  const t = useT();
  const c = profileAnswers.character;

  const patch = (partial: Partial<typeof c>) =>
    setProfileAnswers((p) => ({ ...p, character: { ...p.character, ...partial } }));

  const [card, setCard] = useState(0);
  const pos = t("questionnaire.progressLabel", { current: String(card + 1), total: String(TOTAL) });
  const goNext = () => setCard((i) => Math.min(i + 1, TOTAL - 1));
  const goBack = () => setCard((i) => Math.max(i - 1, 0));

  const yesNoCards: {
    question: string;
    key: keyof typeof c;
  }[] = [
    { question: t("character.peacemaker"), key: "peacemaker" },
    { question: t("character.homebuilder"), key: "homebuilder" },
    { question: t("character.humorous"), key: "humorous" },
    { question: t("character.caring"), key: "caringAffectionate" },
    { question: t("character.peacefulHome"), key: "wantsPeacefulHome" },
    { question: t("character.manners"), key: "goodManners" },
  ];

  if (card < 6) {
    const current = yesNoCards[card];
    return (
      <div className="screen-enter" style={{ flex: 1 }}>
        <YesNoCard
          question={current.question}
          pos={pos}
          value={c[current.key] as boolean | null}
          onYes={() => { patch({ [current.key]: true }); goNext(); }}
          onNo={() => { patch({ [current.key]: false }); goNext(); }}
          onBack={goBack}
          showBack={card > 0}
        />
      </div>
    );
  }

  /* ── Sub-card 6 (last): Honesty rating 1–10 ── */
  const HONESTY_STEPS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  return (
    <div className="screen-enter" style={{ flex: 1 }}>
      <QuestionCard
        question={t("character.honestyRating")}
        subtext={t("character.honestyHint")}
        position={pos}
      >
        <div className="q-scale">
          {HONESTY_STEPS.map((n) => (
            <button
              key={n}
              type="button"
              className={`q-scale__btn${c.honestyRating === n ? " is-selected" : ""}`}
              onClick={() => patch({ honestyRating: n })}
              aria-pressed={c.honestyRating === n}
            >
              {n}
            </button>
          ))}
        </div>
        <button type="button" className="q-subnav__back" style={{ marginTop: 16 }} onClick={goBack}>
          ← {t("shell.back")}
        </button>
      </QuestionCard>
      <div className="shell__footer" style={{ paddingLeft: 0, paddingRight: 0 }}>
        <Button onClick={next} disabled={!canContinue}>
          {t("common.continue")}
        </Button>
      </div>
    </div>
  );
}
